import {
  EventBlocks,
  EventBlocksType,
  EventUniqueBlocks,
  IEvent,
  IIconBandSetting,
} from "Bands/types";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { APIService } from "shared/services";
import { IAPIError, IDataKeys, IJSONAPIData, IJSONAPIItem } from "shared/types";
import { getLastIdFromPath } from "shared/utils";
import { EventAccommodationBlockService } from "./EventAccommodationBlockService";
import { EventGuestlistBlockService } from "./EventGuestlistBlockService";
import { EventInfoBlockService } from "./EventInfoBlockService";
import { EventPeopleBlockService } from "./EventPeopleBlockService";
import { EventScheduleBlockService } from "./EventScheduleBlockService";
import { EventTravelBlockService } from "./EventTravelBlockService";
import { EventVenueBlockService } from "./EventVenueBlockService";

interface IJSONAPIEvent {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIEventCollection {
  [IDataKeys.event]: IJSONAPIEvent;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventService {
  static getEvents = (
    queryParams?:
      | string
      | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIEventCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        let page = 1;

        const events: IJSONAPIEventCollection = await APIService.get({
          path: API_ENDPOINTS.EVENTS,
          data: { ...(queryParams as any), page, itemsPerPage: 10 },
        });

        while (page < events.pagination.totalPages) {
          page++;
          const nextPageEvents = await APIService.get({
            path: API_ENDPOINTS.EVENTS,
            data: { ...(queryParams as any), page },
          });

          events.event = {
            ...events.event,
            ...nextPageEvents.event,
          };

          // Get the first (and should be only) key from meta object
          const metaKey = Object.keys(events.meta)[0];
          if (metaKey && events.meta[metaKey]) {
            events.meta[metaKey].data = [
              ...((events.meta[metaKey].data as IJSONAPIItem[]) || []),
              ...((nextPageEvents.meta[metaKey].data as IJSONAPIItem[]) || []),
            ];
          }

          events.pagination = {
            ...events.pagination,
            ...nextPageEvents.pagination,
          };
        }

        resolve(events);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENTS);
        return reject(error);
      }
    });

  static getEvent = (id: string): Promise<IJSONAPIEvent> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT(id),
        });

        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT);
        return reject(error);
      }
    });

  static createEvent = (data: Omit<IEvent, "id">): Promise<IJSONAPIEvent> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENTS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT);
        return reject(error);
      }
    });

  static editEvent = ({
    id,
    data,
    blockTypesToUpdate,
    resendRSVP,
  }: {
    id: string;
    data: Partial<IEvent>;
    blockTypesToUpdate?: EventBlocksType[];
    resendRSVP?: boolean;
  }): Promise<IJSONAPIEvent> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT(id),
          data: {
            ...data,
            blockTypesToUpdate,
            resendRSVP,
          },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT);
        return reject(error);
      }
    });

  static deleteEvent = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT);
        return reject(error);
      }
    });

  static getEventBlocksOfType = async (
    eventId: string,
    type: EventBlocksType,
    infoBlockType?: IIconBandSetting
  ) => {
    let blocks;
    const queryParams = {
      event: API_ENDPOINTS.EVENT(eventId),
      page: 1,
      itemsPerPage: 100,
    };

    switch (type) {
      case EventUniqueBlocks.people:
        blocks = await EventPeopleBlockService.getBlocks(queryParams);
        if (!blocks?.eventPeopleBlock) return [];

        return blocks?.eventPeopleBlock
          ? Object.values(blocks?.eventPeopleBlock).map(({ attributes }) => ({
              hasUsers: !!((attributes?.users || []) as IJSONAPIData[]).some(
                ({ data }) => !!data?.attributes?.rsvp
              ),
              label: attributes?.title as string,
              value: attributes?.id as string,
            }))
          : [];
      case EventUniqueBlocks.venue:
        blocks = await EventVenueBlockService.getBlocks(queryParams);
        if (!blocks?.eventVenueBlock) return [];

        return blocks?.eventVenueBlock
          ? Object.values(blocks?.eventVenueBlock).map(({ attributes }) => ({
              label: attributes?.title as string,
              value: attributes?.id as string,
            }))
          : [];
      case EventBlocks.guestlist:
        blocks = await EventGuestlistBlockService.getBlocks(queryParams);
        if (!blocks?.eventGuestListBlock) return [];

        return blocks?.eventGuestListBlock
          ? Object.values(blocks?.eventGuestListBlock).map(
              ({ attributes }) => ({
                label: attributes?.title as string,
                value: attributes?.id as string,
              })
            )
          : [];
      case EventBlocks.schedule:
        blocks = await EventScheduleBlockService.getBlocks(queryParams);

        if (!blocks?.eventScheduleBlock) return [];

        return blocks?.eventScheduleBlock
          ? Object.values(blocks?.eventScheduleBlock).map(({ attributes }) => ({
              hasEntries: !!((attributes?.entries || []) as IJSONAPIData[])
                .length,
              label: attributes?.title as string,
              value: attributes?.id as string,
            }))
          : [];
      case EventBlocks.accommodation:
        blocks = await EventAccommodationBlockService.getBlocks(queryParams);
        if (!blocks?.eventAccommodationBlock) return [];

        return blocks?.eventAccommodationBlock
          ? Object.values(blocks?.eventAccommodationBlock).map(
              ({ attributes }) => ({
                label: attributes?.title as string,
                value: attributes?.id as string,
              })
            )
          : [];
      case EventBlocks.infoBlock:
        blocks = await EventInfoBlockService.getBlocks(queryParams);
        if (!blocks?.eventInfoBlock) return [];

        if (infoBlockType) {
          blocks = Object.values(blocks?.eventInfoBlock).filter(
            ({ attributes }) => {
              return (
                getLastIdFromPath(
                  (attributes?.type as IJSONAPIData).data.id
                ) === infoBlockType?.id
              );
            }
          );
        }

        return (blocks as IJSONAPIItem[]).map(({ attributes }) => ({
          label: attributes?.title as string,
          value: attributes?.id as string,
        }));
      case EventBlocks.travel:
        blocks = await EventTravelBlockService.getBlocks(queryParams);
        if (!blocks?.eventTravelBlock) return [];

        return blocks?.eventTravelBlock
          ? Object.values(blocks?.eventTravelBlock).map(({ attributes }) => ({
              label: attributes?.title as string,
              value: attributes?.id as string,
            }))
          : [];
      default:
        return Promise.reject();
    }
  };
}
